import React, { useEffect, useState } from 'react';
import { DayEndReport } from './DayEndReport';
import { ClientsReport } from './ClientsReport';
import { UsersReport } from './UsersReport';
import { TasksReport } from './TasksReport';
import { SubTasksReport } from './SubTasksReport';
import { Button } from '@/components/common/Button';
import { Download } from 'lucide-react';
import { reportsApi, ReportParams } from '@/api/endpoints/reports';
import toast from 'react-hot-toast';
import { setConfigPageFunctions, setQuotationsPageFunctions, setReportsPageFunctions } from '@/components/keyboard';
import { useNavigate } from 'react-router-dom';

export const ReportsPage: React.FC = () => {
  const [activeReport, setActiveReport] = useState<string>('day-end');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const navigate = useNavigate();

  const reports = [
    { id: 'day-end', label: 'Day End Report', component: DayEndReport },
    { id: 'clients', label: 'Clients Report', component: ClientsReport },
    { id: 'users', label: 'Users Report', component: UsersReport },
    { id: 'tasks', label: 'Tasks Report', component: TasksReport },
    { id: 'sub-tasks', label: 'Sub-tasks Report', component: SubTasksReport },
  ];

  const dateFilters = [
    { id: 'all', label: 'All Time' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'thisWeek', label: 'This Week' },
    { id: 'thisMonth', label: 'This Month' },
    { id: 'pastWeek', label: 'Past Week' },
    { id: 'lastMonth', label: 'Past Month' },
    { id: 'thisYear', label: 'This Year' },
    { id: 'custom', label: 'Custom Date Range' },
  ];

 useEffect(() => {
    setReportsPageFunctions({
      setActiveTab: (tabId: string) => {
        console.log('⌨️ Switching to tab:', tabId);
        // navigate(`/configurations/${tabId}`);
        setActiveReport(tabId)
      },
    });

    return () => {
      setConfigPageFunctions({
        setActiveTab:null
      });
    };
  }, [navigate]);

  const getReportParams = (): ReportParams => {
    const params: ReportParams = {
      dateType: dateFilter as any,
    };

    if (dateFilter === 'custom' && customDateRange.from && customDateRange.to) {
      params.fromDate = customDateRange.from;
      params.toDate = customDateRange.to;
    }

    return params;
  };

  const handleExport = async () => {
    try {
      const params = getReportParams();
      const blob = await reportsApi.exportReport(activeReport, params);
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeReport}-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export report');
    }
  };

  const ActiveReportComponent = reports.find((r) => r.id === activeReport)?.component || DayEndReport;

  return (
    <div className="h-full bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        {/* Report Tabs */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center space-x-8">
            {reports.map((report) => (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                className={`
                  pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                  ${
                    activeReport === report.id
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }
                `}
              >
                {report.label}
              </button>
            ))}
          </div>
          
          <Button
            variant="primary"
            size="sm"
            icon={<Download size={16} />}
            onClick={handleExport}
          >
            Export Data
          </Button>
        </div>

        {/* Date Filters */}
        <div className="px-6 py-4 flex items-center space-x-2 overflow-x-auto">
          {dateFilters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setDateFilter(filter.id)}
              className={`
                px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap
                ${
                  dateFilter === filter.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="px-6 py-3 flex items-center space-x-4 border-t border-gray-200">
            <input
              type="date"
              value={customDateRange.from}
              onChange={(e) => setCustomDateRange({ ...customDateRange, from: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={customDateRange.to}
              onChange={(e) => setCustomDateRange({ ...customDateRange, to: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
          </div>
        )}
      </div>

      {/* Report Content */}
      <div className="flex h-[calc(100vh-180px)]">
        <div className="flex-1 overflow-hidden">
          <ActiveReportComponent dateFilter={dateFilter} customDateRange={customDateRange} />
        </div>
      </div>
    </div>
  );
};